import { gql, useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  ArtistArchiveOrganizationNotificationMutation,
  ArtistArchiveOrganizationNotificationMutationVariables,
  ConsoleOfficialInformationConnectionFragmentDoc,
  ConsoleOfficialInformationsQuery,
  ConsoleOfficialInformationsQueryVariables,
  FetchNewConsoleOfficialInformationsQuery,
  FetchNewConsoleOfficialInformationsQueryVariables,
  OfficialInformationConsoleBoxFragmentDoc,
  OrganizationNotificationStatus,
} from '@/clients/utoniq-core/schema'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const [isFilterByPublished, setIsFilterByPublished] = useState<boolean>(false)

  const { isFirstLoadRef } = useGetNewestData<
    FetchNewConsoleOfficialInformationsQuery,
    FetchNewConsoleOfficialInformationsQueryVariables
  >({
    query: FETCH_NEW_OFFICIAL_INFORMATION,
    variables: {
      status: [
        OrganizationNotificationStatus.Published,
        /**
         * @description
         * Open when function is implemented in the future
         * https://app.clickup.com/t/908127/UT-17780
         */
        // OrganizationNotificationStatus.Scheduled,
      ],
    },
    fields: [
      {
        fragmentName: 'OfficialInformationConsoleBox',
        fieldName: 'artistOrganizationOfficialNotifications',
        fragment: OfficialInformationConsoleBoxFragmentDoc,
      },
    ],
  })

  const { data, loading, fetchMore } = useQuery<
    ConsoleOfficialInformationsQuery,
    ConsoleOfficialInformationsQueryVariables
  >(QUERY, {
    variables: {
      status: [
        OrganizationNotificationStatus.Published,
        /**
         * @description
         * Open when function is implemented in the future
         * https://app.clickup.com/t/908127/UT-17780
         */
        // OrganizationNotificationStatus.Scheduled,
      ],
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      isFirstLoadRef.current = false
    },
  })

  const [archiveOrganizationNotification] = useMutation<
    ArtistArchiveOrganizationNotificationMutation,
    ArtistArchiveOrganizationNotificationMutationVariables
  >(ARCHIVE_ORGANIZATION_NOTIFICATION)

  const isNextFetch = !!data

  return {
    state: {
      artistId: user?.uid ?? '',
      loading: loading && !isNextFetch,
      hasNextPage:
        !!data?.artistOrganizationOfficialNotifications?.pageInfo?.hasNextPage,
      isFilterByPublished,
      officialInformations:
        data?.artistOrganizationOfficialNotifications ?? null,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            status: [
              OrganizationNotificationStatus.Published,
              /**
               * @description
               * Open when function is implemented in the future
               * https://app.clickup.com/t/908127/UT-17780
               */
              // OrganizationNotificationStatus.Scheduled,
            ],
            pageInput: {
              cursor:
                data?.artistOrganizationOfficialNotifications?.pageInfo
                  ?.endCursor,
            },
          },
        })
      },
      onFilterByPublished() {
        setIsFilterByPublished(prevState => !prevState)
      },
      async onDelete(id) {
        await archiveOrganizationNotification({ variables: { id } })
      },
    },
  }
})

const QUERY = gql`
  query ConsoleOfficialInformations(
    $status: [OrganizationNotificationStatus!]
    $pageInput: PageInput
  ) {
    artistOrganizationOfficialNotifications(
      status: $status
      pageInput: $pageInput
    ) {
      ...ConsoleOfficialInformationConnection
    }
  }
  ${ConsoleOfficialInformationConnectionFragmentDoc}
`

const FETCH_NEW_OFFICIAL_INFORMATION = gql`
  query FetchNewConsoleOfficialInformations(
    $status: [OrganizationNotificationStatus!]
  ) {
    artistOrganizationOfficialNotifications(status: $status) {
      ...ConsoleOfficialInformationConnection
    }
  }
  ${ConsoleOfficialInformationConnectionFragmentDoc}
`

const ARCHIVE_ORGANIZATION_NOTIFICATION = gql`
  mutation ArtistArchiveOrganizationNotification($id: ID!) {
    artistArchiveOrganizationNotification(id: $id) {
      ...OfficialInformationConsoleBox
    }
  }
  ${OfficialInformationConsoleBoxFragmentDoc}
`
